"use server";

/**
 * ────────────────────────────────────────────────────────────────────────────
 * SERVER ACTIONS DE AUTH — login (magic link simulado), registro, demo, logout.
 * ────────────────────────────────────────────────────────────────────────────
 * Auth LOCAL, sin proveedor externo. Mapeable a Supabase Auth después:
 * `requestMagicLink` sería `supabase.auth.signInWithOtp`, el route handler de
 * verificación sería el callback, y `setSession` guardaría el JWT de Supabase
 * en vez del nuestro. Las superficies no se enteran.
 */
import { redirect } from "next/navigation";
import { headers } from "next/headers";
import { getDb } from "@/db/client";
import { getUserByEmail, getUserById } from "@/db/domains/identity/users.repo";
import { closeAccount, registerClient } from "@/db/domains/identity/users.service";
import { rateLimit } from "@/lib/rate-limit";
import { signMagicLink } from "./magic-link";
import {
  getActiveSessionUser,
  setSession,
  clearSession,
  roleHomePath,
  loginPath,
  toSessionUser,
} from "./server";

// ── 1) Magic link (simulado) ────────────────────────────────────────────────

export interface MagicLinkState {
  // `not_found` se mantiene en el union por compat de la UI, pero la action YA
  // NO lo devuelve (ver anti-enumeración abajo).
  status: "idle" | "sent" | "not_found";
  email?: string;
  /** SÓLO en dev: link de acceso mostrado en pantalla (en prod iría por email). */
  devLink?: string;
}

/** IP del request (primer hop de `x-forwarded-for`), para las claves de rate limit. */
async function requestIp(): Promise<string> {
  const h = await headers();
  const fwd = h.get("x-forwarded-for");
  return fwd?.split(",")[0]?.trim() || h.get("x-real-ip") || "unknown";
}

/**
 * Paso 1 del login: el usuario pone su email. Si existe en `users`, firmamos un
 * magic link corto. En dev lo devolvemos para mostrarlo en pantalla (banner
 * "modo demo"); en prod se enviaría por email y no se devolvería.
 *
 * ENDURECIDO (#6):
 *  - Anti-enumeración: SIEMPRE devuelve `status:"sent"`, exista o no el email
 *    (antes `not_found` revelaba qué correos tienen cuenta). En prod la respuesta
 *    es indistinguible; el devLink sólo aparece en dev y sólo si el user existe.
 *    La UI ya ofrece "Crear cuenta" de forma incondicional, no dependía de este
 *    estado.
 *  - Rate limit por (ip+email): frena el brute force del token del magic link
 *    dentro de su ventana de validez. Al exceder, responde igual (`sent`) SIN
 *    firmar ni enviar nada — el atacante no distingue el bloqueo.
 */
export async function requestMagicLinkAction(
  _prev: MagicLinkState,
  formData: FormData,
): Promise<MagicLinkState> {
  const email = String(formData.get("email") ?? "").trim().toLowerCase();
  const locale = normalizeLocale(formData.get("locale"));
  const returnTo = String(formData.get("returnTo") ?? "");

  const ip = await requestIp();
  const throttled = !rateLimit("magicLink", `${ip}:${email}`).allowed;

  // Respuesta uniforme: nunca revela existencia del email ni el estado del límite.
  const uniform: MagicLinkState = { status: "sent", email };
  if (throttled) return uniform;

  const db = await getDb();
  const user = await getUserByEmail(db, email);
  if (!user) return uniform;

  const token = signMagicLink(user.id);
  const qs = new URLSearchParams({ token });
  if (returnTo) qs.set("returnTo", returnTo);
  const devLink = `/${locale}/login/verify?${qs.toString()}`;

  // En prod: await sendMagicLinkEmail(email, devLink); return uniform;
  return {
    status: "sent",
    email,
    devLink: process.env.NODE_ENV === "development" ? devLink : undefined,
  };
}

// ── 2) Registro de cliente nuevo (L0) ───────────────────────────────────────

export interface RegisterState {
  status: "idle" | "error";
  reasonKey?: string;
  values?: { email: string; name: string; dateOfBirth: string };
}

/**
 * Registra un cliente nuevo (role=client, L0) vía el service de identity y lo
 * deja logueado (auto-login). En éxito redirige a /app.
 */
export async function registerClientAction(
  _prev: RegisterState,
  formData: FormData,
): Promise<RegisterState> {
  const email = String(formData.get("email") ?? "").trim();
  const name = String(formData.get("name") ?? "").trim();
  const dateOfBirth = String(formData.get("dateOfBirth") ?? "").trim();
  // Aceptación explícita de Términos + Aviso de privacidad (requisito de tienda:
  // consentimiento informado registrable). El `<input type="checkbox">` manda
  // "on" cuando está marcado; ausente si no.
  const acceptedTerms = formData.get("acceptTerms") === "on";
  const locale = normalizeLocale(formData.get("locale"));
  const values = { email, name, dateOfBirth };

  // #6 — frena alta masiva scripteada por IP.
  const ip = await requestIp();
  if (!rateLimit("register", ip).allowed) {
    return { status: "error", reasonKey: "auth.register.rate_limited", values };
  }

  // Guardia de servidor: sin aceptar Términos + Privacidad no se crea la cuenta
  // (no confiar sólo en el `required` del checkbox del cliente).
  if (!acceptedTerms) {
    return { status: "error", reasonKey: "auth.register.terms_required", values };
  }

  const db = await getDb();
  const result = await registerClient(db, {
    email,
    displayName: name,
    locale,
    dateOfBirth,
  });
  if (!result.ok || !result.user) {
    return { status: "error", reasonKey: result.reasonKey, values };
  }

  await setSession(toSessionUser(result.user));
  redirect(roleHomePath(result.user.role, locale));
}

// ── 3) Selector de usuario demo (SÓLO development) ───────────────────────────

/**
 * Login de un click como un usuario seed. Guardado tras `NODE_ENV` para que no
 * exista en prod. Lo usa el selector demo del login.
 */
export async function loginAsDemoAction(formData: FormData): Promise<void> {
  if (process.env.NODE_ENV !== "development") {
    redirect(loginPath("es"));
  }
  const userId = String(formData.get("userId") ?? "");
  const locale = normalizeLocale(formData.get("locale"));

  const db = await getDb();
  const user = await getUserById(db, userId);
  if (!user) redirect(loginPath(locale));

  await setSession(toSessionUser(user));
  redirect(roleHomePath(user.role, locale));
}

// ── 4) Logout ────────────────────────────────────────────────────────────────

export async function logoutAction(formData: FormData): Promise<void> {
  const locale = normalizeLocale(formData.get("locale"));
  await clearSession();
  redirect(loginPath(locale));
}

// ── 5) Borrado de cuenta (self-service, Apple 5.1.1(v) + Google) ──────────────

/**
 * Borra la cuenta del usuario logueado (anonimización + `status="deleted"`,
 * ver `closeAccount`). Requisito DURO de ambas tiendas para apps con cuenta+PII.
 * Borde de seguridad: re-autoriza con `getActiveSessionUser` (no confía en el
 * proxy) — sólo el dueño de la sesión activa puede cerrar SU cuenta. Tras
 * cerrar, invalida la cookie y manda al login (la cuenta ya no puede reentrar:
 * los gates rechazan `status !== "active"`). La confirmación explícita vive en
 * la UI (`DeleteAccountSection`); acá se re-chequea el checkbox por si acaso.
 */
export async function deleteAccountAction(formData: FormData): Promise<void> {
  const locale = normalizeLocale(formData.get("locale"));
  const confirmed = formData.get("confirm") === "on";

  const user = await getActiveSessionUser();
  if (!user) {
    // Sin sesión activa no hay nada que borrar: a login.
    redirect(loginPath(locale));
  }
  if (!confirmed) {
    // Guardia de servidor: sin confirmación explícita no se borra.
    redirect(`/${locale}/${user.role === "therapist" ? "pro" : "app"}/profile`);
  }

  const db = await getDb();
  await closeAccount(db, user.userId);
  await clearSession();
  redirect(loginPath(locale));
}

// ── helpers ──────────────────────────────────────────────────────────────────

function normalizeLocale(raw: FormDataEntryValue | null): "es" | "en" {
  return raw === "en" ? "en" : "es";
}
